/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import TablePageSelector from 'components/Ui/Tanstack/Actions/TablePageSelector'
import TablePageSize from 'components/Ui/Tanstack/Actions/TablePageSize'
import UiPaginationButtons from 'components/Ui/UiPaginationButtons'
import clsx from 'clsx'
import {TableInitBase} from 'utils/hooks/useTable'

interface TableFootProps {
    colSpan?: number
    tableInit: TableInitBase
    paginationIdPrefix?: string
}

const TableFoot = (props: TableFootProps) => {
    const { t } = useTranslation()
    const ALL_PAGE_SIZE_OPTION = 0
    const currentPageSize = props.tableInit.table.getState().pagination.pageSize
    const fallbackCount = props.tableInit.table.getFilteredRowModel().rows.length
    const totalRecords =
        typeof props.tableInit.recordsCount === 'number'
            ? props.tableInit.recordsCount
            : fallbackCount
    const allPageSize = totalRecords > 0 ? totalRecords : currentPageSize
    const selectedPageSize =
        totalRecords > 0 && currentPageSize >= totalRecords
            ? ALL_PAGE_SIZE_OPTION
            : currentPageSize

    const rawPageCount = props.tableInit.table.getPageCount()
    const hasRows = totalRecords > 0 || props.tableInit.recordsTable.length > 0
    const safePageCount =
        Number.isFinite(rawPageCount) && rawPageCount > 0
            ? rawPageCount
            : hasRows
            ? 1
            : 0

    const safeSelectedPage =
        safePageCount > 0
            ? Math.min(props.tableInit.table.getState().pagination.pageIndex + 1, safePageCount)
            : 0

    const totalColumns = props.tableInit.table
        .getVisibleLeafColumns()
        .filter((column) => column.id !== 'select' && column.id !== 'actions').length

    const handlePageSizeChange = (nextPageSize: number) => {
        if (nextPageSize === ALL_PAGE_SIZE_OPTION) {
            props.tableInit.paginationSetPageSize(allPageSize)
            return
        }

        props.tableInit.paginationSetPageSize(nextPageSize)
    }

    return (
        <div
            className={clsx(
                'TableFoot',
                'select-none',
                'bg-white/90 backdrop-blur-md',
                'border-t border-gray-200/80 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]',
                'dark:bg-gray-950/90 dark:border-gray-800/80 dark:text-gray-200',
                'px-3 md:px-4 xl:px-6 py-3 xl:py-4'
            )}
        >
            <div className={clsx('flex flex-col lg:flex-row items-center justify-between gap-3 lg:gap-4')}>
                {/* Left side: Page selector and total records */}
                <div className={clsx('flex flex-row items-center gap-3 xl:gap-6')}>
                    {safePageCount > 0 && <div className={clsx('flex flex-row items-center')}>
                        <span className={clsx('mr-3 text-gray-500 dark:text-gray-400 font-medium')}>{t('Page')}</span>
                        <TablePageSelector
                            disabled={props.tableInit.isLoading}
                            pageCount={safePageCount}
                            selected={safeSelectedPage}
                            onChange={props.tableInit.paginationSetPageIndexHandler}
                        />
                        <span className={clsx('ml-3 text-gray-400 dark:text-gray-500 font-medium')}>
                            {t('of')}{' '}
                            <span className="text-gray-900 dark:text-gray-100 font-bold">
                                {safePageCount}
                            </span>
                        </span>
                    </div>}

                    <div className={clsx('hidden sm:flex h-4 w-px bg-gray-200 dark:bg-gray-800')} />

                    <div className={clsx('text-gray-500 dark:text-gray-400 font-medium')}>
                        {t('Total')}:{' '}
                        <span className="text-gray-900 dark:text-gray-100 font-bold">
                            {typeof props.tableInit.recordsCount === 'number' ? props.tableInit.recordsCount : '0'}
                        </span>
                    </div>

                    {import.meta.env.DEV ? (
                        <>
                            <div className={clsx('hidden sm:flex h-4 w-px bg-gray-200 dark:bg-gray-800')} />
                            <div
                                className={clsx(
                                    'text-xs font-medium text-amber-700/80 dark:text-amber-300/80',
                                    'font-mono tracking-tight'
                                )}
                                title="Dev helper: visible data columns in the table"
                            >
                                TotalColumns:{' '}
                                <span className="font-bold">{totalColumns}</span>
                            </div>
                        </>
                    ) : null}
                </div>

                {/* Right side: page size + pagination (shared height via .TableFoot-control) */}
                <div className={clsx('flex flex-row items-center gap-2 xl:gap-4 w-full lg:w-auto justify-between lg:justify-end')}>
                    <TablePageSize
                        disabled={props.tableInit.isLoading}
                        pageSizes={[20, 50, 100, ALL_PAGE_SIZE_OPTION]}
                        selected={selectedPageSize}
                        onChange={handlePageSizeChange}
                        idPrefix={props.paginationIdPrefix}
                        optionLabels={{ [ALL_PAGE_SIZE_OPTION]: `${t('All')} (∞)` }}
                    />
                    <UiPaginationButtons
                        disabledPrev={safePageCount === 0 || !props.tableInit.table.getCanPreviousPage() || props.tableInit.isLoading}
                        disabledNext={safePageCount === 0 || !props.tableInit.table.getCanNextPage() || props.tableInit.isLoading}
                        onFirst={() => props.tableInit.paginationSetPageIndexHandler(0)}
                        onPrev={props.tableInit.paginationPreviousPageHandler}
                        onNext={props.tableInit.paginationNextPageHandler}
                        onLast={() =>
                            props.tableInit.paginationSetPageIndexHandler(
                                safePageCount > 0 ? safePageCount - 1 : 0
                            )
                        }
                        idPrefix={props.paginationIdPrefix}
                    />
                </div>
            </div>
        </div>
    )
}

export default TableFoot
